import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orgApi } from '../services/api';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Kanban,
  Calendar,
  Building2,
  UserCog,
  Settings,
  BarChart3,
  LogOut,
  Bell,
  ChevronDown,
  TrendingUp,
  Activity,
  ClipboardList,
  LifeBuoy,
  Layers,
  Trophy,
  Users2,
  Inbox,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import GlobalSearch from './GlobalSearch';
import BuildBadge from './common/BuildBadge';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/jobs', icon: Briefcase, label: 'Jobs' },
  { path: '/candidates', icon: Users, label: 'Candidates' },
  { path: '/pipeline', icon: Kanban, label: 'Pipeline' },
  { path: '/interviews', icon: Calendar, label: 'Interviews' },
  { path: '/clients', icon: Building2, label: 'Clients' },
  { path: '/command-center', icon: ClipboardList, label: 'Command Center', roles: ['admin', 'delivery_manager', 'team_lead', 'recruiter'] },
  { path: '/workbench', icon: Inbox, label: 'My Workbench', roles: ['admin', 'delivery_manager', 'team_lead', 'recruiter'] },
  { path: '/job-allocation', icon: Layers, label: 'Job Allocation', roles: ['admin', 'delivery_manager', 'team_lead'] },
  { path: '/my-team', icon: Users2, label: 'My Team', roles: ['admin', 'delivery_manager', 'team_lead'] },
  { path: '/my-performance', icon: Trophy, label: 'My Performance', roles: ['admin', 'delivery_manager', 'team_lead', 'recruiter'] },
  { path: '/users', icon: UserCog, label: 'Users', roles: ['admin', 'team_lead', 'delivery_manager'] },
  { path: '/analytics', icon: TrendingUp, label: 'Analytics', roles: ['admin', 'delivery_manager'] },
  { path: '/activity-logs', icon: Activity, label: 'Activity Logs', roles: ['admin'] },
  { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['admin', 'delivery_manager'] },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    let alive = true;
    if (user?.role !== 'admin') return;
    orgApi
      .getMine()
      .then((r) => {
        if (alive && r.data?.is_default) setIsPlatformAdmin(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems
    .filter((item) => !item.roles || item.roles.includes(user?.role))
    .concat(
      isPlatformAdmin
        ? [
            {
              path: '/admin/tickets',
              icon: LifeBuoy,
              label: 'Support Tickets',
            },
          ]
        : []
    );

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'delivery_manager':
        return 'bg-purple-100 text-purple-700';
      case 'team_lead':
        return 'bg-blue-100 text-blue-700';
      case 'recruiter':
        return 'bg-green-100 text-green-700';
      case 'client_spoc':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col fixed h-full z-30" data-testid="sidebar">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-700 rounded-lg flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              InterviewWala
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-auto py-2 px-3"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-teal-100 text-teal-700 text-sm font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <Badge className={`mt-1 ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role?.replace('_', ' ')}
                </Badge>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <BuildBadge user={user} />
      </aside>

      {/* Main content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <header className="h-16 glass-header sticky top-0 z-10 flex items-center gap-4 px-8" data-testid="header">
          <div className="flex-1 max-w-md">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" />
            </Button>
          </div>
        </header>

        {/* Page content — capped at 1600px so wide monitors get a deliberate canvas
            instead of edge-to-edge sprawl, but still much wider than the marketing
            site since dashboards benefit from more horizontal room. */}
        <main className="px-6 sm:px-8 xl:px-12 py-8" data-testid="main-content">
          <div className="max-w-[1600px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
